// Gọi máy chủ cho Phòng máy (QĐ 0541). Hai luật riêng của mảng này:
//   · Nội dung phòng không nằm lại bộ đệm sau khi rời màn: staleTime 0, gcTime 0.
//   · Mỗi lần gọi một cửa là máy chủ ghi một dòng sổ — thẻ chỉ gọi khi đang mở.
// Lời máy chủ hiện nguyên văn qua serverErrorMessage (Luật 6).
import { useEffect } from "react";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api/client";
import { serverErrorMessage } from "@/lib/api/error-message";
import type { components } from "@/lib/api/generated/schema";
import { ACTION } from "@/lib/auth/actions";
import { hasPermission } from "@/lib/authv2/permissions";
import { HUNCH_ROOM_OFF, useHunchRoomFlag } from "./flag";

type S = components["schemas"];
export type HunchMe = S["HunchRoomMe"];
export type HunchOverview = S["HunchRoomOverview"];
export type RulebookSummary = S["HunchRulebookSummary"];
export type UploadResult = S["HunchRulebookUploadResult"];
export type HunchProblem = S["HunchProblem"];
export type HunchMember = S["HunchMember"];
export type AddMemberInput = S["HunchMemberAddInput"];
export type LogEntry = S["HunchLogEntry"];
export type LogPage = S["HunchLogPage"];
export type TryInput = S["HunchTryInput"];
export type TryResult = S["HunchTryResult"];
export type TryPoint = S["HunchTryPoint"];
export type TryOption = S["HunchTryOption"];
export type TryFact = S["HunchTryFact"];
export type LogGroup = "" | "edit" | "try" | "denied";

export const LOG_PAGE = 50;

export const hunchKeys = {
  all: ["hunch-room"] as const,
  me: ["hunch-room", "me"] as const,
  overview: ["hunch-room", "overview"] as const,
  rulebooks: ["hunch-room", "rulebooks"] as const,
  members: ["hunch-room", "members"] as const,
  log: (group: LogGroup) => ["hunch-room", "log", group] as const,
};

const noCache = { staleTime: 0, gcTime: 0, retry: false } as const;

export function useHunchRoomMe(enabled: boolean) {
  return useQuery({
    queryKey: hunchKeys.me,
    enabled,
    staleTime: 30_000,
    queryFn: async () => {
      const { data, error } = await api.GET("/hunch-room/me");
      if (error || !data) throw new Error(serverErrorMessage(error, "Không tải được Phòng máy"));
      return data;
    },
  });
}

/** Nạp trạng thái Phòng máy cho cổng menu. */
export function useHunchRoomMeSync(loggedIn: boolean) {
  const allowed = loggedIn && hasPermission(ACTION.HunchRoomEnter);
  const q = useHunchRoomMe(allowed);
  const set = useHunchRoomFlag((s) => s.set);
  const d = allowed ? q.data : undefined;
  useEffect(() => {
    set(d ? { enabled: d.enabled, isMember: d.isMember, isChief: d.isChief } : HUNCH_ROOM_OFF);
  }, [d, set]);
}

export function useOverview(enabled: boolean) {
  return useQuery({
    queryKey: hunchKeys.overview,
    enabled,
    ...noCache,
    queryFn: async () => {
      const { data, error } = await api.GET("/hunch-room/overview");
      if (error || !data) throw new Error(serverErrorMessage(error, "Không mở được Phòng máy"));
      return data;
    },
  });
}

export function useRulebooks(enabled: boolean) {
  return useQuery({
    queryKey: hunchKeys.rulebooks,
    enabled,
    ...noCache,
    queryFn: async () => {
      const { data, error } = await api.GET("/hunch-room/rulebooks");
      if (error || !data) throw new Error(serverErrorMessage(error, "Không tải được danh sách bản luật"));
      return data;
    },
  });
}

export function useMembers(enabled: boolean) {
  return useQuery({
    queryKey: hunchKeys.members,
    enabled,
    ...noCache,
    queryFn: async () => {
      const { data, error } = await api.GET("/hunch-room/members");
      if (error || !data) throw new Error(serverErrorMessage(error, "Không tải được người vào phòng"));
      return data;
    },
  });
}

/** Sổ xem/sửa: mỗi trang 50 dòng, trang sau lấy các dòng cũ hơn dòng nhỏ nhất đang có. */
export function useLog(group: LogGroup, enabled: boolean) {
  return useInfiniteQuery({
    queryKey: hunchKeys.log(group),
    enabled,
    ...noCache,
    initialPageParam: 0,
    getNextPageParam: (last: LogPage) =>
      last.entries.length < LOG_PAGE ? undefined : Math.min(...last.entries.map((e) => e.id)),
    queryFn: async ({ pageParam }) => {
      const query: { group?: LogGroup; before?: number; limit: number } = { limit: LOG_PAGE };
      if (group) query.group = group;
      if (pageParam) query.before = pageParam;
      const { data, error } = await api.GET("/hunch-room/log", { params: { query } });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không tải được sổ"));
      return data;
    },
  });
}

export function useTry() {
  return useMutation({
    mutationFn: async (input: TryInput) => {
      const { data, error } = await api.POST("/hunch-room/try", { body: input });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không thử được đơn này"));
      return data;
    },
  });
}

export function useUpload() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { label: string; content: string }) => {
      const { data, error } = await api.POST("/hunch-room/rulebooks", { body });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không nạp được bản luật"));
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: hunchKeys.all }),
  });
}

export function useActivate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data, error } = await api.POST("/hunch-room/rulebooks/{id}/activate", { params: { path: { id } } });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không bật được bản luật"));
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: hunchKeys.all }),
  });
}

export function useAddMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: AddMemberInput) => {
      const { data, error } = await api.POST("/hunch-room/members", { body });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không thêm được người vào phòng"));
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: hunchKeys.all }),
  });
}

export function useRemoveMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (staffId: string) => {
      const { data, error } = await api.DELETE("/hunch-room/members/{staffId}", { params: { path: { staffId } } });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không gỡ được người khỏi phòng"));
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: hunchKeys.all }),
  });
}
